// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useState, MouseEvent, useEffect } from 'react';
import styled from '@emotion/styled';
import {
  fontSize,
  isMediaMax,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Icon from 'presentations/views/components/atoms/Icon';
import DrawerMenu from 'presentations/views/components/organisms/DrawerMenu';
import Link from 'presentations/views/components/atoms/Link';
import Logo from 'presentations/views/components/atoms/Logo';
import Button from 'presentations/views/components/atoms/Button';
import {
  homePath,
  pricingPath,
  resourceListPath,
  loginPath,
  featureListPath,
  downloadsPath,
  dashboardPath,
  signUpPath,
  productIntegrationsPath,
  newsListPath,
  contactPath,
  bestPracticesListPath,
  productSecurityPath,
  customerStoryListPath,
  changelogListPath,
} from 'presentations/routes/path';
import { black, colorPalette, gray01 } from 'presentations/views/styles/color';
import Image from 'presentations/views/components/atoms/Image';
import { ViewerItem } from 'presentations/api/schema/user';

import { authRedirectUrl } from 'presentations/routes/helper';
import { useRouter } from 'next/router';
import { developerPath } from 'presentations/routes/path/developer';

interface Props {
  currentUrl: string;
  viewer: ViewerItem | null;
  isDrawerOpen: boolean;
  isDarkable?: boolean;
  onClick: any;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const [to, setTo] = useState<string>(authRedirectUrl(homePath()));
  const router = useRouter();
  const locale = router.locale;
  useEffect(() => {
    setTo(authRedirectUrl(window.location.pathname + window.location.search));
  }, []);

  const isMobile = isMediaMax('tablet');
  if (!isMobile) return null;

  const onClickDrawer = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    props.onClick(!props.isDrawerOpen);
  };

  const onCloseDrawer = () => {
    props.onClick(false);
  };

  const userMenuList = [
    {
      label: t('Product'),
      icon: <ItemIcon name="home" />,
      link: homePath(),
    },
    {
      label: t('Feature'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" width={10} />,
      link: featureListPath(),
    },
    {
      label: t('Downloads'),
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" width={10} />,
      link: downloadsPath(),
    },
    {
      label: t('Integrations'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" width={10} />,
      link: productIntegrationsPath(),
    },
    {
      label: t('Security'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" width={10} />,
      link: productSecurityPath(),
    },
    {
      label: t('Developers'),
      undefined,
      icon: <ItemIcon name="console" />,
      link: developerPath(),
    },
    {
      label: t('Pricing'),
      icon: <ItemIcon name="list_alt_rounded" />,
      link: pricingPath(),
    },
  ];
  userMenuList.push(
    {
      label: t('Resource'),
      icon: (
        <ItemIcon
          name="description_outlined"
          className="material-icons-outlined"
        />
      ),
      link: resourceListPath(),
    },
    {
      label: t('Customer Stories'),
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" width={10} />,
      link: customerStoryListPath(),
    },
    {
      label: t('Changelog'),
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" width={10} />,
      link: changelogListPath(),
    },
    {
      label: t('News'),
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" width={10} />,
      link: newsListPath(),
    },
    {
      label: t('Magazine'),
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" width={10} />,
      link: resourceListPath(),
    },
    {
      label: t('Best Practices'),
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" width={10} />,
      link: bestPracticesListPath(),
    },
  );
  userMenuList.push({
    label: t('Contact'),
    icon: <ItemIcon name="email-outline" />,
    link: contactPath(),
  });

  // viewer

  return (
    <>
      <AccountSelector onClick={onClickDrawer}>
        <StyledIcon
          name="dehaze"
          color={props.isDarkable ? colorPalette.black.dark : undefined}
        />
      </AccountSelector>
      <DrawerMenu
        isOpen={props.isDrawerOpen}
        onClose={onCloseDrawer}
        menuList={userMenuList}
        direction="left"
        header={
          <MenuHeader>
            <Link href={homePath()}>
              <ServiceImage width={130} height={34} />
            </Link>
          </MenuHeader>
        }
        footer={
          <MenuFooter>
            <FooterButtonContainer>
              <FooterLoginButton
                href={loginPath({ to: dashboardPath() })}
                variant="outlined"
                color="primary"
              >
                {t('Login')}
              </FooterLoginButton>
            </FooterButtonContainer>
            <FooterButtonContainer>
              <FooterSignUpButton
                href={signUpPath({ to: dashboardPath() })}
                color="primary"
              >
                {t('Sign Up')}
              </FooterSignUpButton>
            </FooterButtonContainer>
          </MenuFooter>
        }
      />
    </>
  );
}

const AccountSelector = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const ItemIcon = styled(Icon)`
  font-size: ${fontSize(24)} !important;
  color: ${gray01};
`;
const ListIcon = styled(Image)`
  margin-left: ${spacing(16)};
`;

const ServiceImage = styled(Logo)`
  width: 130px;

  ${mediaMax.tablet} {
    width: 89px;
  }
`;

const MenuHeader = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px;
  border-bottom: ${spacing(16)} 0;
  margin: ${spacing(8)} 0;
`;

const MenuFooter = styled.div`
  padding: ${spacing(16)};
  margin-bottom: ${spacing(8)};
`;
const FooterButtonContainer = styled.div`
  margin-bottom: ${spacing(8)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const FooterLoginButton = styled(Button)`
  width: 100%;
  background-color: transparent;
`;

const FooterSignUpButton = styled(Button)`
  width: 100%;
`;

const StyledIcon = styled(Icon)<{ color?: string }>`
  color: ${(props) => props.color || black};
`;
